import type { UsageProviderKind } from "@cmmd-forge/contracts";
import { useCallback, useLayoutEffect, useRef } from "react";

import type { DayColumn } from "./usageChartGeometry";
import { PROVIDER_PRESENTATION } from "./usageProviders";

/**
 * Places the hover readout next to the cursor, flipped to whichever side of it
 * has room.
 *
 * Placement is written to CSS custom properties on the plot rather than to
 * state: measuring the tooltip to place it would otherwise re-render the very
 * element being measured on every pointer move.
 */
export function useTooltipPlacement(
  plotRef: React.RefObject<HTMLDivElement | null>,
  hoverIndex: number | null,
) {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  const place = useCallback(() => {
    const target = placementTarget(plotRef.current, tooltipRef.current, pointerRef.current);
    if (target === undefined) return;
    const { plot, tooltip, pointer } = target;

    plot.style.setProperty(
      "--usage-tooltip-left",
      `${axisOffset(pointer.x, tooltip.offsetWidth, plot.clientWidth)}px`,
    );
    plot.style.setProperty(
      "--usage-tooltip-top",
      `${axisOffset(pointer.y, tooltip.offsetHeight, plot.clientHeight)}px`,
    );
  }, [plotRef]);

  useLayoutEffect(() => {
    if (hoverIndex === null) return;
    place();

    const boxes = observableBoxes(plotRef.current, tooltipRef.current);
    if (boxes === undefined) return;

    // Either box can change size without the pointer moving — a wider number, a
    // resized window — and a stale placement would hang off the plot.
    const observer = new ResizeObserver(place);
    observer.observe(boxes.plot);
    observer.observe(boxes.tooltip);
    return () => observer.disconnect();
  }, [hoverIndex, place, plotRef]);

  const trackPointer = (clientX: number, clientY: number, bounds: DOMRect | undefined) => {
    if (bounds === undefined) return;
    pointerRef.current = {
      x: clamp(clientX - bounds.left, bounds.width),
      y: clamp(clientY - bounds.top, bounds.height),
    };
    place();
  };

  return {
    tooltipRef,
    trackPointer,
    clearPointer: () => {
      pointerRef.current = null;
    },
  };
}

/**
 * The two boxes worth observing, or nothing when either is absent or the
 * environment has no ResizeObserver (jsdom, older Safari).
 */
function observableBoxes(
  plot: HTMLDivElement | null,
  tooltip: HTMLDivElement | null,
): { plot: HTMLDivElement; tooltip: HTMLDivElement } | undefined {
  if (plot === null || tooltip === null || typeof ResizeObserver === "undefined") return undefined;
  return { plot, tooltip };
}

/** The three things placement needs, or nothing if any of them is absent yet. */
function placementTarget(
  plot: HTMLDivElement | null,
  tooltip: HTMLDivElement | null,
  pointer: { x: number; y: number } | null,
):
  | { plot: HTMLDivElement; tooltip: HTMLDivElement; pointer: { x: number; y: number } }
  | undefined {
  if (plot === null || tooltip === null || pointer === null) return undefined;
  return { plot, tooltip, pointer };
}

function clamp(value: number, max: number): number {
  return Math.min(max, Math.max(0, value));
}

/** Offset on one axis: past the pointer if it fits, before it if it does not. */
function axisOffset(pointer: number, size: number, extent: number): number {
  const gap = 12;
  const preferred = pointer + gap + size <= extent ? pointer + gap : pointer - gap - size;
  return clamp(preferred, Math.max(0, extent - size));
}

interface UsageChartTooltipProps {
  readonly ref: React.Ref<HTMLDivElement>;
  readonly column: DayColumn | undefined;
  readonly providers: readonly UsageProviderKind[];
  readonly periodLabel: string;
  readonly format: (value: number) => string;
}

/**
 * Per-lane readout for the hovered period, plus its total.
 *
 * Placed by CSS custom properties the plot sets: the parent owns the geometry,
 * so measuring the tooltip never re-renders it.
 */
export function UsageChartTooltip({
  ref,
  column,
  providers,
  periodLabel,
  format,
}: UsageChartTooltipProps) {
  return (
    <div
      ref={ref}
      className="dropdown-glass pointer-events-none absolute z-10 max-w-full min-w-36 rounded-xl border border-border/50 px-2.5 py-2 text-xs shadow-lg"
      style={{
        left: "var(--usage-tooltip-left, 0px)",
        top: "var(--usage-tooltip-top, 0px)",
      }}
    >
      <div className="mb-1 text-muted-foreground">{periodLabel}</div>
      {providers.map((provider) => {
        const { label, mark: Mark } = PROVIDER_PRESENTATION[provider];
        const band = column?.bands.find((entry) => entry.provider === provider);
        return (
          <div key={provider} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Mark className="size-3 shrink-0" aria-hidden />
              {label}
            </span>
            <span className="text-foreground tabular-nums">{format(band?.value ?? 0)}</span>
          </div>
        );
      })}
      <div className="mt-1 flex items-center justify-between gap-3 border-t border-border pt-1">
        <span className="text-muted-foreground">Total</span>
        <span className="text-foreground tabular-nums">{format(column?.total ?? 0)}</span>
      </div>
    </div>
  );
}
